import React, { useEffect } from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import {
  ensureTenantId,
  hasResolvedTenantId,
  resolveTenantIdFromHost,
} from "./contexts/AuthContext";
import { COLORS } from "./constants";
import { brandConfig } from "./config/brand";
import { initLiff } from "./config/liff";
import { TenantNotFoundScreen } from "./components/TenantNotFoundScreen";
import { TenantResolvingScreen } from "./components/TenantResolvingScreen";
import { installGlobalErrorHandlers } from "./utils/helper/error-reporter";

const applyGlobalColorVariables = () => {
  const root = document.documentElement;

  root.style.setProperty("--color-background-app", brandConfig.palette.bodyBackground);
  root.style.setProperty("--color-app-header-bg", COLORS.background.appSecondary);
  root.style.setProperty("--color-common-white", COLORS.common.white);
  root.style.setProperty("--color-app-link", COLORS.brand.gradientStart);
  root.style.setProperty("--color-slick-dot", COLORS.text.onDarkPlaceholder);
  root.style.setProperty("--color-slick-dot-active", COLORS.background.accentMuted);
};

// The HTML boot shell (index.html) shows a spinner until React takes over
// #root; its inline watchdog must stand down once the app is really alive.
const markAppBooted = (): void => {
  try {
    globalThis.window.__appBoot?.done?.();
  } catch {
    // no-op
  }
};

// Mounted next to every tree this file renders. Its effect runs only after
// React has COMMITTED that tree — i.e. the shell has actually been replaced.
// Calling markAppBooted() right after root.render() would be too early: render
// is only scheduled there, and a tree that throws during its first render
// never replaces the shell, so the watchdog must stay armed for that case.
const BootMarker = () => {
  useEffect(() => {
    markAppBooted();
  }, []);
  return null;
};

// Persist tenantId BEFORE any LIFF redirect happens.
// LINE rich-menu URL carries ?tanantId=xxx (or ?tenantId=xxx) on the very
// first open. LIFF may redirect to OAuth and the query string is lost on
// the return trip, so we must save it to localStorage right here.
// First: anything that throws during boot below should already be reported.
installGlobalErrorHandlers();
const cameFromLink = ensureTenantId();
applyGlobalColorVariables();

const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
const vconsoleMode = import.meta.env.VITE_MODE;

// vconsole (~280 KB of source) used to be a static import that every member
// downloaded before anything rendered, and production created a hidden
// instance on every mobile visit. Now development/staging get the visible
// console after boot; production loads nothing — the long-press gesture
// (useLongPressVConsole) downloads and opens it on demand, so a member's log
// starts at the moment support asks them to long-press.
if (
  isMobile &&
  (vconsoleMode === "development" || vconsoleMode === "staging")
) {
  void import("vconsole")
    .then(({ default: VConsole }) => {
      const instance = new VConsole();
      instance.setOption("maxLogNumber", 1000);
      globalThis.window.__vconsoleInstance__ = instance;
    })
    .catch(() => {
      // debug console only — never let it affect boot
    });
}

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);

const renderSplash = () => {
  root.render(
    <>
      <BootMarker />
      <TenantResolvingScreen />
    </>
  );
};

const render = () => {
  root.render(
    <React.StrictMode>
      <BootMarker />
      {hasResolvedTenantId() ? <App /> : <TenantNotFoundScreen />}
    </React.StrictMode>
  );
};

// Hostname is the only tenant signal on a shared hosting site (many gyms, one
// build), so ask the server unless this load already carried a ?tenantId=.
const startApp = () => {
  if (cameFromLink) {
    render();
  } else if (hasResolvedTenantId()) {
    render(); // warm start: revalidate in the background, never block paint
    void resolveTenantIdFromHost();
  } else {
    // cold start: the app is unusable without a tenant, so wait for the answer —
    // behind a splash, not a blank page (the lookup can take up to its 5s cap).
    renderSplash();
    void resolveTenantIdFromHost().then(render);
  }
};

// LIFF handoff. On a cold LINE entry the LIFF server lands the member on the
// endpoint URL with ?liff.state=<real path + query>; the LINE SDK then does a
// real location.replace to that URL (and never resolves liff.init on this
// page). Booting the whole app here — Firebase session restore, /me/profile —
// is work the replace kills mid-flight, which surfaced as false "profile
// failed" errors and a flash of the retry screen on every LINE entry. So keep
// the splash up, start the SDK so it can redirect, and only fall back to the
// full app when the SDK fails (no liffId, network) or finishes without
// redirecting (state already equals this URL), or after a safety window.
const LIFF_HANDOFF_FALLBACK_MS = 10_000;

const isLiffHandoffPage = (): boolean => {
  try {
    return new URLSearchParams(globalThis.location.search).has("liff.state");
  } catch {
    return false;
  }
};

if (isLiffHandoffPage()) {
  renderSplash();
  let started = false;
  const startOnce = () => {
    if (started) return;
    started = true;
    globalThis.clearTimeout(fallbackTimer);
    startApp();
  };
  const fallbackTimer = globalThis.setTimeout(startOnce, LIFF_HANDOFF_FALLBACK_MS);
  // initLiff caches the pending init, so LiffProvider later awaits this same
  // promise instead of calling liff.init twice; a rejected init is not cached
  // and LiffProvider's own attempt reports it as today (failed_init).
  initLiff().then(startOnce, startOnce);
} else {
  startApp();
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
